import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Disclosure, Transition } from '@headlessui/react';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import useDisplaySettings from 'lib/hooks/useDisplaySettings';

import Icon from 'components/shared/Icon/Icon';
import Allowed from 'components/shared/Permissions/Allowed';
import Typography from 'components/shared/Typography/Typography';

import { userState as user_state } from '../../state';

const buildPath = (...paths) => paths.filter(Boolean).join('/');

const MultiSectionSidebarList = ({ list, expanded, target }) => {
  const { pathname } = useLocation();
  const userState = useRecoilValue(user_state);
  const { saveDisplaySettings } = useDisplaySettings(userState);
  const [hoveredGroup, setHoveredGroup] = useState(null);

  const handleOpen = useCallback(
    (path, isOpen) => {
      saveDisplaySettings({
        ...userState.display_settings,
        sidebars: {
          ...userState.display_settings?.sidebars,
          [target]: { ...userState.display_settings?.sidebars?.[target], [path]: { open: !isOpen } }
        }
      });
    },
    [saveDisplaySettings, target, userState.display_settings]
  );

  const renderItem = useCallback(
    (item, mainParentPath = '', parentPath = '') => {
      const fullPath = buildPath(mainParentPath, parentPath, item.path);

      const isActive = pathname.endsWith(`/${fullPath}`);

      const isHovered = hoveredGroup === fullPath;

      return (
        <Allowed key={fullPath} requiredPermissions={item?.permission}>
          {item.children ? (
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button
                    data-qa={`sidebar-${fullPath}`}
                    className="group flex w-full items-center justify-between p-2"
                    onClick={() => handleOpen(fullPath, open)}
                    onMouseEnter={() => setHoveredGroup(fullPath)}
                    onMouseLeave={() => setHoveredGroup(null)}>
                    <Typography
                      icon={item.icon}
                      iconColor={isActive || isHovered ? 'primary' : 'neutral'}
                      iconSize={18}
                      color={isActive || isHovered ? 'primary-500' : 'neutral-700'}
                      className="cursor-pointer"
                      iconClassName="cursor-pointer">
                      {item.name}
                    </Typography>
                    <Icon
                      icon="chevron-down-linear"
                      color={isActive || isHovered ? 'primary' : 'neutral'}
                      className={cs('transition-transform duration-100', open && 'rotate-180')}
                    />
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0">
                    <Disclosure.Panel className="ml-4 border-l border-neutral-200">
                      {item.children.map((child) => renderItem(child, '', fullPath))}
                    </Disclosure.Panel>
                  </Transition>
                </>
              )}
            </Disclosure>
          ) : (
            <NavLink
              className="flex w-full items-center p-2"
              to={fullPath}
              onMouseEnter={() => setHoveredGroup(fullPath)}
              onMouseLeave={() => setHoveredGroup(null)}>
              <Typography
                icon={item.icon}
                iconColor={isActive || isHovered ? 'primary' : 'neutral'}
                iconSize={18}
                color={isActive || isHovered ? 'primary' : 'neutral-700'}
                className="cursor-pointer"
                iconClassName="cursor-pointer">
                {item.name}
              </Typography>
            </NavLink>
          )}
        </Allowed>
      );
    },
    [pathname, hoveredGroup, handleOpen, setHoveredGroup]
  );

  const renderedSections = useMemo(() => {
    if (!list || !expanded) return null;

    return list.map((section) => (
      <div key={section.title} className="flex flex-col gap-2">
        {/* TODO: Remove the 'Allowed' component once the Reports section is completed. */}
        <Allowed requiredPermissions={section?.permission}>
          <Typography variant="label" size="xs" color="neutral-500" className="pl-2">
            {section.title.toUpperCase()}
          </Typography>
        </Allowed>
        <div className="h-full overflow-auto pr-1">
          {section.children.map((item) => renderItem(item, section.path))}
        </div>
      </div>
    ));
  }, [list, expanded, renderItem]);

  return <div className="flex h-full flex-col gap-4">{renderedSections}</div>;
};

export default MultiSectionSidebarList;
